'use client';

import * as React from 'react';

import { cn } from '../lib/utils';
import { useIsMobile } from '../hooks/use-mobile';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from './sheet';

/**
 * A dialog that becomes a bottom sheet on a phone, which is where a modal
 * belongs on touch — a centred dialog taller than the viewport clips its own
 * footer, putting the submit button out of reach.
 *
 * The two primitives own separate contexts, so the root has to swap along with
 * the content; that is what this context carries. Compose it exactly like
 * `Dialog`: root, content, header, title, description, footer.
 */
const ResponsiveDialogContext = React.createContext(false);

function ResponsiveDialog({
  open,
  onOpenChange,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}) {
  const isMobile = useIsMobile();
  const Root = isMobile ? Sheet : Dialog;

  return (
    <ResponsiveDialogContext.Provider value={isMobile}>
      <Root open={open} onOpenChange={onOpenChange}>
        {children}
      </Root>
    </ResponsiveDialogContext.Provider>
  );
}

function ResponsiveDialogContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof DialogContent>) {
  const isMobile = React.useContext(ResponsiveDialogContext);

  if (!isMobile) {
    return (
      <DialogContent className={className} {...props}>
        {children}
      </DialogContent>
    );
  }

  return (
    // p-0 mirrors DialogContent: the header, body and footer each pay their own
    // padding, so adding any here would double it.
    <SheetContent side="bottom" className="max-h-[92svh] gap-0 rounded-t-2xl p-0">
      <div className={cn('flex flex-col overflow-y-auto', className)}>{children}</div>
    </SheetContent>
  );
}

function ResponsiveDialogHeader(props: React.ComponentProps<typeof DialogHeader>) {
  const isMobile = React.useContext(ResponsiveDialogContext);
  return isMobile ? (
    <SheetHeader {...props} className={cn('px-4 py-5', props.className)} />
  ) : (
    <DialogHeader {...props} />
  );
}

function ResponsiveDialogTitle(props: React.ComponentProps<typeof DialogTitle>) {
  const isMobile = React.useContext(ResponsiveDialogContext);
  return isMobile ? <SheetTitle {...props} /> : <DialogTitle {...props} />;
}

function ResponsiveDialogDescription(props: React.ComponentProps<typeof DialogDescription>) {
  const isMobile = React.useContext(ResponsiveDialogContext);
  return isMobile ? <SheetDescription {...props} /> : <DialogDescription {...props} />;
}

function ResponsiveDialogFooter(props: React.ComponentProps<typeof DialogFooter>) {
  const isMobile = React.useContext(ResponsiveDialogContext);
  return isMobile ? (
    <SheetFooter
      {...props}
      className={cn('flex-col-reverse gap-2 border-t border-border px-4 py-4', props.className)}
    />
  ) : (
    <DialogFooter {...props} />
  );
}

export {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
};
